/**
 * Render looks by space: whole Photoreal render settings (photoRender.ts —
 * lighting, HDRI, sky, textures, materials, effects, lens) saved under a name
 * for a kind of space (garage, workshop, laundry room …) in Prop Studio's
 * Look Lab. Kept in the props library's manifest (`looks`, saved through
 * Prop Studio's back end like the finishes), so they live on the site, not in
 * a browser.
 *
 * When an estimate's room is sent to the render queue, the SERVER picks one
 * of the space's enabled looks by the estimate (render-jobs-lib.php
 * rj_pick_look: the same estimate, the same look) and renders the room with
 * it, whatever the customer's browser sent: the room, its products and its
 * staged props are theirs; how it is lit and finished is the look's.
 * `pickLook` here is that same pick, for the Look Lab's preview of it.
 */

import { normalizePhotoRender, PhotoRenderSettings } from './photoRender';
import { SPACE_ID } from './renderFinish';

export interface RenderLook {
  id: string;
  name: string;
  /** Off: kept, but never picked for a customer's render. */
  enabled: boolean;
  photo: PhotoRenderSettings;
  savedAt?: string;
  /** The Look Lab's last test render of it (a /renders/… picture URL), if any. */
  picture?: string;
}

/** By space type (presets.ts SPACE_TYPES ids). */
export type LookPools = Record<string, RenderLook[]>;

/** A look's id as the back ends take it (server.js LOOK_ID). */
export const LOOK_ID = /^[a-z0-9-]{1,40}$/;
/** The most looks a space holds. */
export const LOOK_MAX = 30;
export const LOOK_NAME_MAX = 80;

const PICTURE_URL = /^\/renders\/[A-Za-z0-9/_.?=&-]{1,300}$/;

/** A look as saved (its settings normalised), or null when it is not one. */
export function renderLook(raw: unknown): RenderLook | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const e = raw as Record<string, unknown>;
  if (typeof e.id !== 'string' || !LOOK_ID.test(e.id)) return null;
  const name = typeof e.name === 'string' ? e.name.trim().slice(0, LOOK_NAME_MAX) : '';
  if (!name) return null;
  if (!e.photo || typeof e.photo !== 'object' || Array.isArray(e.photo)) return null;
  return {
    id: e.id,
    name,
    enabled: e.enabled !== false,
    photo: normalizePhotoRender(e.photo),
    ...(typeof e.savedAt === 'string' ? { savedAt: e.savedAt } : {}),
    ...(typeof e.picture === 'string' && PICTURE_URL.test(e.picture) ? { picture: e.picture } : {}),
  };
}

/** The pools as the library sends them: bad looks dropped, spaces with none left out. */
export function lookPools(raw: unknown): LookPools {
  const out: LookPools = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [space, list] of Object.entries(raw as Record<string, unknown>)) {
    if (!SPACE_ID.test(space) || !Array.isArray(list)) continue;
    const seen = new Set<string>();
    const looks = list.map(renderLook).filter((l): l is RenderLook => !!l && !seen.has(l.id) && !!seen.add(l.id)).slice(0, LOOK_MAX);
    if (looks.length) out[space] = looks;
  }
  return out;
}

/**
 * The look a space's render gets: one of its enabled looks, by the estimate's
 * guid. The same rule as the server's (render-jobs-lib.php rj_pick_look):
 * crc32 of the guid, modulo the number of enabled looks, in the saved order.
 */
export function pickLook(space: string, pools: LookPools | undefined, guid: string): RenderLook | undefined {
  const enabled = (pools?.[space] ?? []).filter((l) => l.enabled);
  if (!enabled.length) return undefined;
  return enabled[crc32(guid) % enabled.length];
}

/** A new look's id from its name, not one the space already has. */
export function newLookId(name: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || 'look';
  if (!used.has(base)) return base;
  for (let n = 2; ; n++) {
    const id = `${base}-${n}`;
    if (!used.has(id)) return id;
  }
}

/** PHP's crc32 (the IEEE polynomial), unsigned. */
export function crc32(text: string): number {
  let c = 0xffffffff;
  // UTF-8 bytes, as PHP hashes them (a guid is plain ASCII anyway).
  const bytes = unescape(encodeURIComponent(text));
  for (let i = 0; i < bytes.length; i++) {
    c ^= bytes.charCodeAt(i);
    for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xedb88320 & -(c & 1));
  }
  return (c ^ 0xffffffff) >>> 0;
}
